import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { ScanStatsCI, ScanStatsPoint } from '../api/types'
import { ErrorBanner, formatDateTime, Spinner, StatCard } from './common'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Statistiche delle scansioni: cosa compare e cosa non si trova più, nel tempo.
 *
 * Un inventario vivo si legge dai cambiamenti, non dai totali. Due numeri
 * (nuovi / scomparsi negli ultimi 7 giorni), la curva per giorno, e gli elenchi
 * degli ultimi CI comparsi e scomparsi — ognuno con il link alla scheda.
 *
 * `compact` è la versione del widget in dashboard: stessi dati, elenchi più
 * corti e senza selettore di finestra. La pagina dedicata usa la versione
 * intera.
 */
export function ScanStats({ compact = false, days = 30 }: { compact?: boolean; days?: number }) {
  const dati = useAsync(() => api.scanStats(days), [days])

  if (dati.loading && !dati.data) return <Spinner />
  if (dati.error) return <ErrorBanner error={dati.error} />
  const d = dati.data
  if (!d) return null

  const quanti = compact ? 5 : 10

  return (
    <div className="scanstats">
      <div className="scanstats__kpi">
        <StatCard label={t('Comparsi (7g)')} value={d.summary.new_7d}
                  tone={d.summary.new_7d > 0 ? 'ok' : undefined} />
        <StatCard label={t('Non più trovati (7g)')} value={d.summary.gone_7d}
                  tone={d.summary.gone_7d > 0 ? 'warn' : undefined} />
        {!compact && <StatCard label={t('Attivi')} value={d.summary.active} />}
        {!compact && <StatCard label={t('Silenti (stale)')} value={d.summary.stale}
                               tone={d.summary.stale > 0 ? 'warn' : undefined} />}
      </div>

      <Curva serie={d.series} />

      <div className="scanstats__liste">
        <ElencoCI titolo={t('Comparsi di recente')} righe={d.recent_new.slice(0, quanti)}
                  vuoto={t('Nessun CI nuovo nel periodo.')} tono="ok" />
        <ElencoCI titolo={t('Non più trovati')} righe={d.recent_gone.slice(0, quanti)}
                  vuoto={t('Niente è sparito: buon segno.')} tono="warn" mostraStato />
      </div>
    </div>
  )
}

/** Il grafico a barre per giorno: comparsi verso l'alto, scomparsi verso il
 *  basso, sulla stessa base — così crescita e perdita si leggono a colpo d'occhio. */
function Curva({ serie }: { serie: ScanStatsPoint[] }) {
  const W = 100
  const H = 40
  const max = Math.max(1, ...serie.map((p) => Math.max(p.new, p.gone)))
  const n = serie.length || 1
  const passo = W / n
  const larg = Math.max(0.6, passo * 0.38)

  if (serie.every((p) => p.new === 0 && p.gone === 0)) {
    return <p className="scanstats__nograf">{t('Nessun cambiamento nel periodo.')}</p>
  }

  return (
    <svg className="scanstats__curva" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         role="img" aria-label={t('Comparsi e scomparsi per giorno')}>
      <line x1="0" y1={H / 2} x2={W} y2={H / 2} className="scanstats__asse" />
      {serie.map((p, i) => {
        const x = i * passo + passo / 2
        const hNew = (p.new / max) * (H / 2 - 1)
        const hGone = (p.gone / max) * (H / 2 - 1)
        return (
          <g key={p.date}>
            {p.new > 0 && (
              <rect x={x - larg} y={H / 2 - hNew} width={larg} height={hNew}
                    className="scanstats__bar scanstats__bar--new">
                <title>{`${p.date}: +${p.new}`}</title>
              </rect>
            )}
            {p.gone > 0 && (
              <rect x={x + 0.1} y={H / 2} width={larg} height={hGone}
                    className="scanstats__bar scanstats__bar--gone">
                <title>{`${p.date}: -${p.gone}`}</title>
              </rect>
            )}
          </g>
        )
      })}
    </svg>
  )
}

function ElencoCI({ titolo, righe, vuoto, tono, mostraStato = false }: {
  titolo: string
  righe: ScanStatsCI[]
  vuoto: string
  tono: 'ok' | 'warn'
  mostraStato?: boolean
}) {
  return (
    <div className="scanstats__col">
      <div className={`scanstats__col-titolo scanstats__col-titolo--${tono}`}>{titolo}</div>
      {righe.length === 0 ? (
        <p className="field__hint" style={{ margin: 0 }}>{vuoto}</p>
      ) : (
        <ul className="scanstats__ul">
          {righe.map((r) => (
            <li key={r.id}>
              <Link to={r.to}>{r.name}</Link>
              <span className="scanstats__meta">
                {r.ci_type}
                {mostraStato && r.state ? ` · ${statoEtichetta(r.state)}` : ''}
                {r.at ? ` · ${formatDateTime(r.at)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function statoEtichetta(state: string): string {
  if (state === 'missing') return t('non trovato')
  if (state === 'retired') return t('dismesso')
  return state
}
